import React, { useEffect, useMemo, useState } from "react";
import { api, inr, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, X, Search, Star, Upload, Package } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const empty = {
  name: "", slug: "", description: "", short_description: "", brand: "",
  image_url: "", images: [], primary_image_index: 0,
  items: [], mrp: 0, price: 0,
  seo_title: "", seo_description: "", seo_keywords: "",
  active: true,
};

export default function AdminCombos() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [pickerQuery, setPickerQuery] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/combos").then((r) => setCombos(r.data));
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data));
  }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const alreadyAdded = new Set(form.items.map((i) => i.product_id));
  const filteredProducts = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    let list = products.filter((p) => !alreadyAdded.has(p.id));
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q));
    return list.slice(0, 20);
  }, [pickerQuery, products, alreadyAdded]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.name),
        mrp: +form.mrp,
        price: +form.price,
        image_url: form.images[form.primary_image_index] || form.images[0] || form.image_url || "",
        primary_image_index: Math.min(Math.max(0, +form.primary_image_index || 0), Math.max(0, form.images.length - 1)),
      };
      if (editing) await api.put(`/admin/combos/${editing}`, payload);
      else await api.post("/admin/combos", payload);
      toast.success("Combo saved");
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({
      ...empty,
      ...c,
      images: c.images?.length ? c.images : (c.image_url ? [c.image_url] : []),
    });
  };

  const del = async (c) => {
    if (!window.confirm(`Delete combo "${c.name}"?`)) return;
    await api.delete(`/admin/combos/${c.id}`);
    load();
  };

  const addItem = (p) => {
    setForm((f) => ({ ...f, items: [...f.items, { product_id: p.id, qty: 1 }] }));
    setPickerQuery("");
  };
  const setItemQty = (pid, qty) => {
    setForm((f) => ({ ...f, items: f.items.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, +qty || 1) } : i) }));
  };
  const rmItem = (pid) => setForm((f) => ({ ...f, items: f.items.filter((i) => i.product_id !== pid) }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addImageUrl = () => {
    const url = window.prompt("Paste an image URL");
    if (url) setForm((f) => ({ ...f, images: [...f.images, url] }));
  };

  const removeImage = (i) => {
    setForm((f) => {
      const imgs = f.images.filter((_, j) => j !== i);
      let pi = f.primary_image_index || 0;
      if (i === pi) pi = 0;
      else if (i < pi) pi = Math.max(0, pi - 1);
      return { ...f, images: imgs, primary_image_index: pi };
    });
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold">Combos & bundles</h1>
      <p className="text-hyd-text2 text-sm">Pack multiple products at a discounted price.</p>

      <div className="grid md:grid-cols-5 gap-5 mt-5">
        <div className="md:col-span-3 space-y-3">
          {combos.map((c) => (
            <div key={c.id} className="bg-hyd-panel border border-hyd-border rounded-2xl p-4 flex gap-3">
              <div className="w-16 h-16 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0">
                {(c.images?.[c.primary_image_index || 0] || c.image_url) && (
                  <img src={imageUrl(c.images?.[c.primary_image_index || 0] || c.image_url)} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-hyd-text2 mt-1">{c.items?.length || 0} items · <span className="text-hyd-gold">{inr(c.price)}</span> · MRP {inr(c.mrp)}</div>
                {c.short_description && <div className="text-xs text-hyd-text3 mt-1 line-clamp-1">{c.short_description}</div>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => edit(c)} className="text-hyd-gold hover:bg-hyd-panel">Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(c)} className="text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="md:col-span-2 bg-hyd-panel border border-hyd-border rounded-3xl p-4 space-y-3 h-fit md:sticky md:top-4">
          <h3 className="font-display text-lg font-bold">{editing ? "Edit combo" : "New combo"}</h3>
          <F label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></F>
          <F label="Short description (1 line)"><Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} maxLength={140} /></F>
          <F label="Full description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
          <div className="grid grid-cols-2 gap-2">
            <F label="MRP (₹)"><Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></F>
            <F label="Price (₹)"><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required /></F>
          </div>
          <F label="Brand (optional)"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></F>

          {/* Multi-image */}
          <div>
            <Label className="text-xs text-hyd-text2 mb-1 block">Images · click ★ to set primary</Label>
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((im, i) => (
                <div key={i} className={`relative aspect-square rounded-xl overflow-hidden border-2 ${i === form.primary_image_index ? "border-hyd-gold" : "border-hyd-border"}`}>
                  <img src={imageUrl(im)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm({ ...form, primary_image_index: i })} title="Set as primary" className={`absolute top-1 left-1 w-5 h-5 grid place-items-center rounded-full ${i === form.primary_image_index ? "bg-hyd-gold text-hyd-bg" : "bg-hyd-bg/80 text-hyd-text2 hover:text-hyd-gold"}`}>
                    <Star className={`w-2.5 h-2.5 ${i === form.primary_image_index ? "fill-hyd-bg" : ""}`} />
                  </button>
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-hyd-bg/80 rounded-full text-hyd-red">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-xl border-2 border-dashed border-hyd-border grid place-items-center text-[10px] text-hyd-text2 hover:border-hyd-gold cursor-pointer">
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                <div className="text-center">
                  <Upload className="w-4 h-4 mx-auto text-hyd-gold" />
                  <div className="mt-1">{uploading ? "..." : "Add"}</div>
                </div>
              </label>
            </div>
            <button type="button" onClick={addImageUrl} className="text-[11px] text-hyd-text2 hover:text-hyd-gold mt-1">+ Image URL</button>
          </div>

          {/* Items — searchable */}
          <div>
            <Label className="text-xs text-hyd-text2 mb-1 block">Items in this combo</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-hyd-text3" />
              <Input
                data-testid="admin-combo-item-search"
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Type to search products…"
                className="pl-8 bg-hyd-bg border-hyd-border h-9 rounded-xl"
              />
            </div>
            {(pickerQuery.trim() || filteredProducts.length > 0) && (
              <div className="mt-1 border border-hyd-border rounded-xl overflow-hidden bg-hyd-bg max-h-52 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-hyd-text3">No products match.</div>
                ) : (
                  filteredProducts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`admin-combo-item-suggest-${p.id}`}
                      onClick={() => addItem(p)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-hyd-panel text-left border-b border-hyd-border last:border-b-0"
                    >
                      <div className="w-8 h-8 rounded-lg bg-hyd-panel2 overflow-hidden shrink-0">
                        {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs truncate">{p.name}</div>
                        <div className="text-[10px] text-hyd-text3">{inr(p.price)}</div>
                      </div>
                      <Plus className="w-3.5 h-3.5 text-hyd-gold" />
                    </button>
                  ))
                )}
              </div>
            )}

            <div className="mt-2 space-y-1 max-h-52 overflow-auto pr-1">
              {form.items.map((it) => {
                const p = productMap[it.product_id];
                return (
                  <div key={it.product_id} className="flex items-center gap-2 bg-hyd-bg border border-hyd-border rounded-xl p-2 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-hyd-panel2 overflow-hidden shrink-0">
                      {p?.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="flex-1 line-clamp-1 text-xs">{p?.name || it.product_id}</span>
                    <Input type="number" min={1} className="w-14 h-7 bg-hyd-panel border-hyd-border text-hyd-text text-xs" value={it.qty} onChange={(e) => setItemQty(it.product_id, e.target.value)} />
                    <button type="button" onClick={() => rmItem(it.product_id)} className="text-hyd-red p-1"><X className="w-3 h-3" /></button>
                  </div>
                );
              })}
              {form.items.length === 0 && <div className="text-xs text-hyd-text3">No items added yet · search above</div>}
            </div>
          </div>

          {/* SEO */}
          <div className="pt-2 border-t border-hyd-border">
            <Label className="text-[10px] uppercase text-hyd-gold tracking-widest font-bold">SEO</Label>
            <div className="mt-2 space-y-2">
              <F label="SEO title"><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} maxLength={70} /></F>
              <F label="SEO description"><Textarea rows={2} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} maxLength={160} /></F>
              <F label="SEO keywords (comma separated)"><Input value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} /></F>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} /> Active
          </label>
          <Button data-testid={ADMIN.comboSave} type="submit" className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full">
            {editing ? "Save changes" : (<><Plus className="w-4 h-4 mr-1" />Create combo</>)}
          </Button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="w-full text-xs text-hyd-text2 hover:text-hyd-gold">Cancel</button>
          )}
        </form>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text [&_textarea]:bg-hyd-bg [&_textarea]:border-hyd-border [&_textarea]:text-hyd-text">{children}</div>
    </div>
  );
}
